import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { FormNotice } from "@/components/site/Notices";
import ConsentCheckbox from "@/components/site/ConsentCheckbox";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema } from "@/components/seo/schema";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM",
  "3:00 PM", "4:00 PM", "5:00 PM",
];

const SERVICES = [
  "Web Development",
  "Web & Mobile App Development",
  "Custom CRM",
  "CMS Software",
  "API Integration",
  "AI Integration",
  "Hosting & Maintenance",
  "Other",
];

function todayStart() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  business_name: "",
  service_interested_in: "",
  preferred_date: "",
  preferred_time: "",
  project_description: "",
};

export default function Consultation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const svc = params.get("service");
    if (svc) {
      // Try to match to one of the dropdown options
      const match = SERVICES.find((s) => svc.toLowerCase().includes(s.toLowerCase().split(" ")[0]));
      setForm((f) => ({ ...f, service_interested_in: match || "Other" }));
    }
  }, [location.search]);

  const setField = (k) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const k of Object.keys(EMPTY)) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please complete every field. Use N/A or Unknown where it does not apply.");
        return;
      }
    }
    if (!consent) {
      toast.error("Please review and agree to the Terms & Conditions to continue.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/consultations", { ...form, consent_agreement: true });
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Could not book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-consultation">
      <Seo
        title="Book a Free Consultation | Equinox Systems"
        description="Book a free 15-minute consultation with Equinox Systems to discuss your goals, the Free Custom Website Program, budget, and timeline. No payment required."
        path="/consultation"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Consultation", path: "/consultation" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <SectionHeader
            as="h1"
            eyebrow="Free 15-Min Consultation"
            title="Book your free consultation"
            description="Schedule a free 15-minute consultation. We'll discuss your goals, requirements, budget, timeline, and the best path forward. No payment required."
          />

          <div className="mb-8">
            <FormNotice />
          </div>

          <form onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 space-y-6" data-testid="consultation-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name *"><Input data-testid="consult-name" value={form.name} onChange={setField("name")} className="bg-white/5 border-white/10 text-white" placeholder="Your name" /></Field>
              <Field label="Email *"><Input data-testid="consult-email" type="email" value={form.email} onChange={setField("email")} className="bg-white/5 border-white/10 text-white" placeholder="you@business.com" /></Field>
              <Field label="Phone *"><Input data-testid="consult-phone" value={form.phone} onChange={setField("phone")} className="bg-white/5 border-white/10 text-white" placeholder="(555) 000-1234" /></Field>
              <Field label="Business Name *"><Input data-testid="consult-business" value={form.business_name} onChange={setField("business_name")} className="bg-white/5 border-white/10 text-white" placeholder="Company name or N/A" /></Field>
              <Field label="Service Interested In *">
                <Select value={form.service_interested_in} onValueChange={setField("service_interested_in")}>
                  <SelectTrigger data-testid="consult-service" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>{SERVICES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Preferred Date *">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="consult-date"
                      className="w-full flex items-center gap-2 rounded-md bg-white/5 border border-white/10 text-left text-white px-3 h-10 text-sm hover:border-white/20 transition-colors"
                    >
                      <CalendarIcon className="w-4 h-4 text-blue-400 shrink-0" />
                      {form.preferred_date ? (
                        format(new Date(`${form.preferred_date}T00:00:00`), "EEE, MMM d, yyyy")
                      ) : (
                        <span className="text-gray-500">Pick a date</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" data-testid="consult-date-popover">
                    <Calendar
                      mode="single"
                      selected={form.preferred_date ? new Date(`${form.preferred_date}T00:00:00`) : undefined}
                      onSelect={(d) => d && setForm((f) => ({ ...f, preferred_date: format(d, "yyyy-MM-dd") }))}
                      disabled={(date) => date < todayStart()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>
              <Field label="Preferred Time *">
                <Select value={form.preferred_time} onValueChange={setField("preferred_time")}>
                  <SelectTrigger data-testid="consult-time" className="bg-white/5 border-white/10 text-white">
                    <Clock className="w-4 h-4 mr-2 text-blue-400" />
                    <SelectValue placeholder="Pick a time" />
                  </SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Project Description *">
              <Textarea data-testid="consult-brief" rows={4} value={form.project_description} onChange={setField("project_description")} className="bg-white/5 border-white/10 text-white" placeholder="Brief overview of your project, goals, or questions..." />
            </Field>

            <ConsentCheckbox checked={consent} onChange={setConsent} testid="consult-consent" />

            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="consult-submit-btn">
              {submitting ? "Booking..." : "Book Free Consultation"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-sm">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
